import { SelectOnlyMixin, SelectableMixin, ActiveMixin, LabelMixin } from '@umbraco-ui/uui-base/lib/mixins';
import { defineElement } from '@umbraco-ui/uui-base/lib/registration';
import { demandCustomElement } from '@umbraco-ui/uui-base/lib/utils';
import { LitElement, html, css } from 'lit';
import { property, state } from 'lit/decorators.js';
import { ref } from 'lit/directives/ref.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { UUIEvent } from '@umbraco-ui/uui-base/lib/events';

class UUIMenuItemEvent extends UUIEvent {
}
UUIMenuItemEvent.SHOW_CHILDREN = "show-children";
UUIMenuItemEvent.HIDE_CHILDREN = "hide-children";
UUIMenuItemEvent.CLICK_LABEL = "click-label";

var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
let UUIMenuItemElement = class extends SelectOnlyMixin(
  SelectableMixin(ActiveMixin(LabelMixin("label", LitElement)))
) {
  constructor() {
    super(...arguments);
    this.disabled = false;
    this.showChildren = false;
    this.hasChildren = false;
    this.loading = false;
    this.iconSlotHasContent = false;
    this._labelButtonChanged = (label) => {
      this.selectableTarget = label || this;
    };
    this._iconSlotChanged = (e) => {
      this.iconSlotHasContent = e.target.assignedNodes({ flatten: true }).length > 0;
    };
    this._onCaretClicked = () => {
      const eventName = this.showChildren ? UUIMenuItemEvent.HIDE_CHILDREN : UUIMenuItemEvent.SHOW_CHILDREN;
      const event = new UUIMenuItemEvent(eventName, { cancelable: true });
      this.dispatchEvent(event);
      if (event.defaultPrevented)
        return;
      this.showChildren = !this.showChildren;
    };
    this._onLabelClicked = () => {
      const event = new UUIMenuItemEvent(UUIMenuItemEvent.CLICK_LABEL);
      this.dispatchEvent(event);
    };
  }
  connectedCallback() {
    super.connectedCallback();
    if (!this.hasAttribute("role"))
      this.setAttribute("role", "menu");
    demandCustomElement(this, "uui-symbol-expand");
    demandCustomElement(this, "uui-loader-bar");
  }
  _renderLabelInside() {
    return html` <slot
        name="icon"
        id="icon"
        style=${this.iconSlotHasContent ? "" : "display: none;"}
        @slotchange=${this._iconSlotChanged}></slot>
      ${this.renderLabel()}
      <slot name="badge" id="badge"> </slot>`;
  }
  _renderLabelAsAnchor() {
    if (this.disabled) {
      return html` <span id="label-button" ${ref(this._labelButtonChanged)}>
        ${this._renderLabelInside()}
      </span>`;
    }
    return html` <a
      id="label-button"
      ${ref(this._labelButtonChanged)}
      href=${ifDefined(this.href)}
      target=${ifDefined(this.target || void 0)}
      rel=${ifDefined(
      this.target === "_blank" ? "noopener noreferrer" : void 0
    )}
      @click=${this._onLabelClicked}
      ?disabled=${this.disabled}
      aria-label="${this.label}">
      ${this._renderLabelInside()}
    </a>`;
  }
  _renderLabelAsButton() {
    return html` <button
      id="label-button"
      ${ref(this._labelButtonChanged)}
      @click=${this._onLabelClicked}
      ?disabled=${this.disabled}
      aria-label="${this.label}">
      ${this._renderLabelInside()}
    </button>`;
  }
  render() {
    return html`
      <div id="menu-item" aria-label="menuitem" role="menuitem">
        ${this.hasChildren ? html`<button id="caret-button" @click=${this._onCaretClicked}>
              <uui-symbol-expand ?open=${this.showChildren}></uui-symbol-expand>
            </button>` : ""}
        ${this.href ? this._renderLabelAsAnchor() : this._renderLabelAsButton()}

        <div id="label-button-background"></div>
        ${this.selectOnly === false ? html`<slot id="actions-container" name="actions"></slot>` : ""}
        ${this.loading ? html`<uui-loader-bar id="loader"></uui-loader-bar>` : ""}
      </div>
      ${this.showChildren ? html`<slot></slot>` : ""}
    `;
  }
};
UUIMenuItemElement.styles = [
  css`
      :host {
        box-sizing: border-box;
        display: block;
        --uui-menu-item-child-indent: calc(var(--uui-menu-item-indent, 0) + 1);
        user-select: none;
      }

      #menu-item {
        position: relative;
        padding-left: calc(var(--uui-menu-item-indent, 0) * var(--uui-size-4,12px));
        display: grid;
        grid-template-columns: var(--uui-size-8,24px) 1fr;
        grid-template-rows: 1fr;
        white-space: nowrap;
      }

      /** Not active, not selected, not disabled: */
      :host(:not([active], [selected], [disabled], [select-mode='highlight']))
        #menu-item
        #label-button:hover
        ~ #label-button-background,
      :host(:not([active], [selected], [disabled]))
        #menu-item
        #caret-button:hover {
        background-color: var(--uui-color-surface-emphasis,rgb(250, 250, 250));
      }
      :host(:not([active], [selected], [disabled]))
        #menu-item
        #label-button:hover,
      :host(:not([active], [selected])) #menu-item #caret-button:hover {
        color: var(--uui-color-interactive-emphasis,#3544b1);
      }

      /** Active */
      :host([active]) #label-button,
      :host([active]) #caret-button {
        color: var(--uui-color-current-contrast,#1b264f);
      }
      :host([active]) #label-button-background {
        background-color: var(--uui-color-current,#f5c1bc);
      }
      :host([active]) #label-button:hover ~ #label-button-background,
      :host([active]) #caret-button:hover {
        background-color: var(--uui-color-current-emphasis,rgb(248, 214, 211));
      }

      /** Disabled */
      :host([disabled]) #menu-item {
        color: var(--uui-color-disabled-contrast,#c4c4c4);
        background-color: var(--uui-color-disabled,#f3f3f5);
      }

      /** Selected */
      :host([selected]:not([select-mode='highlight'], [disabled]))
        #label-button,
      :host([selected]:not([select-mode='highlight'], [disabled]))
        #caret-button {
        color: var(--uui-color-selected-contrast,#fff);
      }
      :host([selected]:not([select-mode='highlight'], [disabled]))
        #label-button-background {
        background-color: var(--uui-color-selected,#3544b1);
      }
      /** Selected, not highlight mode */
      :host([selected]:not([select-mode='highlight'], [disabled]))
        #label-button:hover
        ~ #label-button-background,
      :host([selected]:not([select-mode='highlight'], [disabled]))
        #caret-button:hover {
        background-color: var(--uui-color-selected-emphasis,rgb(70, 86, 200));
      }

      /** highlight mode, default */
      :host([select-mode='highlight']:not([disabled], [active], [selectable]))
        #menu-item
        #label-button:hover
        ~ #label-button-background {
        border-radius: var(--uui-border-radius,3px);
        background-color: var(--uui-color-surface-emphasis,rgb(250, 250, 250));
      }

      /** highlight mode, active */
      :host([select-mode='highlight'][active]:not([disabled]))
        #menu-item
        #label-button-background {
        border-radius: var(--uui-border-radius,3px);
      }

      /** highlight mode, active & selected */
      :host([select-mode='highlight'][active][selected]:not([disabled]))
        #menu-item
        #label-button:hover
        ~ #label-button-background {
        border-radius: var(--uui-border-radius,3px);
        background-color: var(--uui-color-current-emphasis,rgb(248, 214, 211));
      }

      /** highlight mode, selected */
      :host([select-mode='highlight'][selected]:not([disabled]))
        #menu-item
        #label-button,
      :host([select-mode='highlight'][selected]:not([disabled]))
        #menu-item
        #caret-button {
        color: var(--uui-color-interactive,#1b264f);
      }
      :host([select-mode='highlight'][selectable][selected]:not([disabled]))
        #menu-item
        #label-button:hover {
        color: var(--uui-color-interactive-emphasis,#3544b1);
      }

      /** highlight mode, selected, selectable caret hover */
      :host([selected][selectable][select-mode='highlight']:not([active]))
        #menu-item
        #caret-button:hover {
        border-radius: var(--uui-border-radius,3px);
        background-color: var(--uui-color-surface-emphasis,rgb(250, 250, 250));
        color: var(--uui-color-interactive-emphasis,#3544b1);
      }

      /** Highlight borders */

      :host([select-mode='highlight']:not([disabled]))
        #menu-item
        #label-button-background::after {
        border-radius: var(--uui-border-radius,3px);
        position: absolute;
        content: '';
        inset: 1px;
        border: 2px solid var(--uui-color-selected,#3544b1);
        opacity: 0;
      }

      :host([select-mode='highlight'][selectable][selected]:not([disabled]))
        #menu-item
        #caret-button:hover::after {
        border-top-left-radius: var(--uui-border-radius,3px);
        border-bottom-left-radius: var(--uui-border-radius,3px);
        position: absolute;
        content: '';
        inset: 1px 0 1px 1px;
        border: 2px solid var(--uui-color-selected,#3544b1);
        border-right: none;
      }

      :host([select-mode='highlight'][selected]:not([disabled]))
        #menu-item
        #label-button-background::after {
        opacity: 1;
      }

      :host([select-mode='highlight'][selectable]:not([disabled]))
        #menu-item
        #label-button:hover
        ~ #label-button-background::after {
        opacity: 0.33;
      }
      :host([select-mode='highlight'][selected]:not([disabled]))
        #menu-item
        #label-button:hover
        ~ #label-button-background::after {
        opacity: 0.66;
      }

      /** Buttons */

      :host([disabled]) #label-button {
        cursor: default;
      }

      button {
        display: inline-flex;
        align-items: center;

        font-family: inherit;
        font-size: inherit;

        padding: 0;
        text-align: left;
        border: none;
        color: inherit;
        background-color: transparent;
        cursor: pointer;
        min-height: var(--uui-size-12,36px);
        z-index: 1;
      }

      #label-button {
        position: relative;
        flex-grow: 1;
        grid-column-start: 2;
        white-space: nowrap;
        overflow: hidden;

        display: inline-flex;
        align-items: center;
        text-decoration: none;
        color: currentColor;
        min-height: var(--uui-size-12,36px);
        z-index: 1;
      }

      #label-button .label {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      span#label-button {
        pointer-events: none; /* avoid hovering state on this. */
      }

      #caret-button + #label-button {
        padding-left: 0;
      }

      #caret-button {
        position: relative;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--uui-color-interactive,#1b264f);
      }

      #label-button-background {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
      }

      #actions-container {
        opacity: 0;
        transition: opacity 120ms;
        grid-column-start: 3;
      }
      :host(:not([disabled])) #menu-item:hover #actions-container,
      :host(:not([disabled])) #menu-item:focus #actions-container,
      :host(:not([disabled])) #menu-item:focus-within #actions-container {
        opacity: 1;
      }

      #loader {
        position: absolute;
        width: 100%;
        bottom: 0;
      }

      #icon {
        display: inline-flex;
        font-size: 16px;
        margin-right: var(--uui-size-2,6px);
      }

      #badge {
        font-size: 12px;
        --uui-badge-position: relative;
        --uui-badge-position: auto;
        display: block;
        margin-left: 6px;
      }

      /** Focus styling */

      :host([select-mode='highlight']) #label-button:focus-visible {
        outline: none;
        overflow: initial;
      }

      :host([select-mode='highlight']) #label-button:focus-visible::after {
        content: '';
        border-radius: calc(var(--uui-border-radius,3px) - 1px);
        position: absolute;
        inset: 3px 3px 3px -5px;
        border: 2px solid var(--uui-color-focus,#3879ff);
      }

      :host([select-mode='highlight']) #caret-button:focus-visible {
        outline: none;
        overflow: initial;
      }

      :host([select-mode='highlight']) #caret-button:focus-visible::after {
        content: '';
        position: absolute;
        inset: 3px;
        border-radius: calc(var(--uui-border-radius,3px) - 1px);
        border: 2px solid var(--uui-color-focus,#3879ff);
      }

      /** Slots */

      slot:not([name]) {
        position: relative;
        display: block;
        width: 100%;
      }
      slot:not([name]) {
        --uui-menu-item-indent: var(--uui-menu-item-child-indent);
      }

      slot[name='actions'] {
        display: flex;
        align-items: center;
        --uui-button-height: calc(var(--uui-size-base-unit) * 4);
        margin-right: var(--uui-size-base-unit);
      }
    `
];
__decorateClass([
  property({ type: Boolean, reflect: true })
], UUIMenuItemElement.prototype, "disabled", 2);
__decorateClass([
  property({ type: Boolean, reflect: true, attribute: "show-children" })
], UUIMenuItemElement.prototype, "showChildren", 2);
__decorateClass([
  property({ type: Boolean, attribute: "has-children" })
], UUIMenuItemElement.prototype, "hasChildren", 2);
__decorateClass([
  property({ type: Boolean, attribute: "loading" })
], UUIMenuItemElement.prototype, "loading", 2);
__decorateClass([
  property({ type: String })
], UUIMenuItemElement.prototype, "href", 2);
__decorateClass([
  property({ type: String })
], UUIMenuItemElement.prototype, "target", 2);
__decorateClass([
  property({ type: String, attribute: "select-mode", reflect: true })
], UUIMenuItemElement.prototype, "selectMode", 2);
__decorateClass([
  state()
], UUIMenuItemElement.prototype, "iconSlotHasContent", 2);
UUIMenuItemElement = __decorateClass([
  defineElement("uui-menu-item")
], UUIMenuItemElement);

export { UUIMenuItemElement, UUIMenuItemEvent };
